//Libraries
import { FC, useEffect, useState } from "react";

//Providers
import { useAiService, useDiService, useOrgService } from "src/services";

//Typing
import { Draft } from "src/types/common";
import { IAIARequest } from "src/types/org/aia";
import { mergeOrg, mergeDi } from "src/types/org/orgInterface";
import { IAIRecord } from "src/types/org/ai/aiInterface";
import { IAIUpdateRequest, ISummary, TAIForm } from "src/types";
import { IOpsRequest } from "src/types/org/operation/requestInterface";

//Processing
import { getFile } from "src/services/schoolRequest";

//Components
import { ErrorNotice, Loader } from "src/generic/component";
import { AiForm } from "./aiEditor";
import { convertToTitleCase, isValidValue } from "./../../../utils/string";
import { IDIRecord, IDIRequest, IDIUpdateRequest } from "src/types/org/di";
import { DiForm } from "./di/diEditor";

const aiDefaults = {
  aiStatusCd: 2,
  stateHomeSchoolInd: false,
  annualReviewInd: false,
  initialIngestionPathCd: 1,
  crossPrgSecHoldInd: false,
};

/**
 * Initialization hook function for the operations form that retrieves
 * all data and maps to interfaces before displaying
 * @param summary
 * @returns
 */
interface IFileReturn<T> {
  request: Draft<IOpsRequest<T>>;
  hints: object;
}

/**
 * Certain fields on the operations form need to be converted to init caps from all caps
 * using same convertToTitleCase utility function as used in ODM
 * @param request
 */
const preProcessData = (request: any) => {
  const { org } = request.request;
  org.name = convertToTitleCase(org.name);
  org.nameShort = isValidValue(org.nameShort) ? convertToTitleCase(org.nameShort) : undefined; //some preexisting old data has no nameShort
  org.nameAbbr = isValidValue(org.nameAbbr) ? convertToTitleCase(org.nameAbbr) : undefined; //some preexisting old data has no nameAbbr
  org.nameShortAbbr = isValidValue(org.nameShortAbbr) ? convertToTitleCase(org.nameShortAbbr) : undefined;
  org.nameNick = isValidValue(org.nameNick) ? convertToTitleCase(org.nameNick) : undefined;
  org.nameFormerOfficial = isValidValue(org.nameFormerOfficial) ? convertToTitleCase(org.nameFormerOfficial) : undefined;
  org.nameRoot = isValidValue(org.nameRoot) ? convertToTitleCase(org.nameRoot) : undefined;
  org.visitingAddress.streetAddr1 = convertToTitleCase(org.visitingAddress.streetAddr1);
  org.visitingAddress.streetAddr2 = org.visitingAddress.streetAddr2 ? convertToTitleCase(org.visitingAddress.streetAddr2) : undefined;
  org.visitingAddress.streetAddr3 = org.visitingAddress.streetAddr3 ? convertToTitleCase(org.visitingAddress.streetAddr3) : undefined;
  org.visitingAddress.city = org.visitingAddress.city ? convertToTitleCase(org.visitingAddress.city) : undefined;

  if (org?.shippingAddress !== undefined) {
    org.shippingAddress.streetAddr1 = convertToTitleCase(org.shippingAddress.streetAddr1);
    org.shippingAddress.streetAddr2 = org.shippingAddress.streetAddr2 ? convertToTitleCase(org.shippingAddress.streetAddr2) : undefined;
    org.shippingAddress.streetAddr3 = org.shippingAddress.streetAddr3 ? convertToTitleCase(org.shippingAddress.streetAddr3) : undefined;
    org.shippingAddress.city = org.shippingAddress.city ? convertToTitleCase(org.shippingAddress.city) : undefined;
    org.shippingAddress.label = org.shippingAddress.label != null
    ? (org.shippingAddress.label === "" ? "" : convertToTitleCase(org.shippingAddress.label))
    : undefined;
  org.shippingAddress.contactName = org.shippingAddress.contactName != null
    ? (org.shippingAddress.contactName === "" ? "" : convertToTitleCase(org.shippingAddress.contactName))
    : undefined;
  org.shippingAddress.contactPhone = org.shippingAddress.contactPhone != null
    ? org.shippingAddress.contactPhone
    : undefined;
  }
};

const useAI = (summary: ISummary) => {
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<IFileReturn<IAIRecord>>({
    request: {
      submitted: undefined,
      request: undefined,
      original: undefined,
      meta: undefined,
    },
    hints: {},
  });
  const { findAi } = useOrgService();
  const { mapAiCreate, mapAiaCreate, mapAiCreateHints, mapAiUpdate, mapNewAiCreateHints,mapNewAiCreate } = useAiService();

  const isV3Summary = (version?: string) => {
    return (version || '').toLowerCase() === 'v3';
  };

  const isV3Submission = (submission: any) => {
    return !!submission?.data?.org;
  };

  useEffect(() => {
    (async () => {
      try {
        if (!summary) throw new Error("Record is required to view form.");

        setLoading(true);

        let hints = {};
        let submitted: Draft<IAIRecord> = {}; //Data submitted by external user
        let original = undefined; //Original data in DB
        let request = undefined; //Current request working data

        const parsedLevel = parseInt(summary.appliedLevel);
        const aiLevelCd = !isNaN(parsedLevel) ? parsedLevel : undefined;

        /**
         * Process the submission file and populate the submitted/hints
         * data structures
         */
        const filePath = summary?.submission;
        if (!filePath) throw new Error("No submission file path, record may be invalid.");
        const submission = await getFile(filePath);
        if (!submission) throw new Error("No submission file returned, record may be invalid.");

        // Due to the fact that hints are always necessary, we should make a common
        // mapping section here to build all the possible datasets, then only check
        // for the draft of the request and only change the form's operational data
        // leaving the rest in tact
        const useNewCreateMapper = isV3Summary(summary.version) || isV3Submission(submission);

        if (summary.appliedType === "ai" && summary.requestType === "create") {
          // Use payload shape + summary version to choose the mapper
          if (useNewCreateMapper) {
            hints = mapNewAiCreateHints(submission);
          } else {
            const form = submission as TAIForm;
            hints = mapAiCreateHints(form);
          }
        } else if (summary.appliedType === "aia" && summary.requestType === "create") {
          const form = submission as IAIARequest;
          hints = {
            org: {
              phone: form.data?.aia?.phone,
              fax: form.data?.aia?.fax,
            },
          };
        } else if (summary.requestType === "update") {
          const request = submission as IAIUpdateRequest;
          const data = request.data;
          hints = {
            ai: {
              merge: data.merge,
            },
            org: {
              phone: data.phone?.formatted,
              fax: data.fax?.formatted,
            },
          };
        }

        /**
         * After Code Control submits a first draft of the data to XPM
         * we don't have to map the application form data to the operation form data
         * as we already have a working copy saved from the draft.  In this case, retrieve
         * the draft data from s3 and set to the working form data.
         *
         * If this is the first time operations form is loaded, we have to convert the divergent
         * datasets coming from the submitter application forms, since they do not share a consistent
         * structure (which they probably should).
         */
        const requestFile = summary?.operationSubmission;
        if (requestFile) {
          request = (await getFile(requestFile)) as IOpsRequest<IAIRecord>;
        } else {
          if (summary.appliedType === "aia" && summary.requestType === "create") {
            submitted = {
              ...(await mapAiaCreate(submission as IAIARequest)),
              ai: { ...aiDefaults, aiLevelCd, aiCategoryCd: 2 },
            };
          } else if (summary.appliedType === "ai" && summary.requestType === "create") {
            const form = submission as TAIForm;
            //console.log("useAI(): Mapping AI Create : form is :", form);
            // Use payload shape + summary version to choose the mapper
            if (useNewCreateMapper) {
              submitted = {
                ...(await mapNewAiCreate(form)),
                ai: { ...aiDefaults, aiLevelCd, aiCategoryCd: 1 },
              };
            } else {
              submitted = {
                ...(await mapAiCreate(form)),
                ai: { ...aiDefaults, aiLevelCd, aiCategoryCd: 1 },
              };
            }
          } else if (summary.requestType === "update") {
            const request = submission as IAIUpdateRequest; //request is submission request from submitter
            const data = request.data; //data is submission
            if (!data) throw new Error("Missing submission data, record is malformed.");
            const mapped = await mapAiUpdate(data); // mapped is user submitted data that is mapped to the operations form
            let aiRecord: IAIRecord = await findAi(summary.aiCode, summary.orgId); //operation form data from DB for org,ai
            console.log(mergeOrg(aiRecord.org || {}, mapped.org || {}, "normal"));
            submitted = {
              org: mergeOrg(aiRecord.org || {}, mapped.org || {}, "normal"),
              ai: {
                ...aiRecord.ai,
                aiLevelCd: mapped.ai?.aiLevelCd,
                aiStatusCd: mapped.ai?.aiStatusCd,
                aiMergedCode: data.merge?.aiCode,
              }, // retrieve AI data for original record and for AI on operation
            };
            original = aiRecord;
          }
          request = {
            submitted: submitted,
            request: submitted,
            original,
          } as IOpsRequest<IAIRecord>;
        }
        preProcessData(request); //to convert certain fields to initcaps from all caps before UI display
        setData({ request: request, hints: hints });
      } catch (error: any) {
        setError(error?.message || error);
      }

      setLoading(false);
    })();
  }, [summary]);

  return { request: data.request, hints: data.hints, isLoading, error };
};

const getDiOrgTypes = (form: IDIRequest) => {
  let instTypeValues = form.data.appForm?.instType || [];
  //If instType field value is Other in S3 file, we need to replace it with string value from instTypeOther field
  if (instTypeValues.includes("Other")) {
    const otherIndex = instTypeValues.indexOf("Other");
    instTypeValues[otherIndex] = form.data.appForm?.instTypeOther || "Other";
  }
  return instTypeValues;
};

const useDI = (summary: ISummary) => {
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState<boolean>(true);
  //IDIRecord is the  JSON that is sent to operation form APIs - prepare, complete
  const [data, setData] = useState<IFileReturn<IDIRecord>>({
    request: {
      submitted: undefined,
      request: undefined,
      original: undefined,
      meta: undefined,
    },
    hints: {},
  });

  const { findDi } = useOrgService();
  const { mapDiCreate, mapDiUpdate} = useDiService();

  useEffect(() => {
    (async () => {
      try {
        if (!summary) throw new Error("Summary Record is required to view form.");

        setLoading(true);

        let hints = {};
        let submitted: Draft<IDIRecord> = {}; //Data submitted by external user in S3 that is captured in Ops request JSON format
        let original = undefined; //Original data in DB
        let request = undefined; //Current request's working data

        /**
         * Process the submission file and populate the submitted/hints
         * data structures
         */
        const filePath = summary?.submission;
        if (!filePath) throw new Error("No submission file path, record may be invalid.");
        const submission = await getFile(filePath);
        if (!submission) throw new Error("No submission file returned, record may be invalid.");

        if (summary?.recordCategoryType === "di" && summary.requestType === "create") {
          const form = submission as IDIRequest;
          hints = {
            //TODO : Need to also grab type for org type display hint
            org: {
              phone: form.data?.org?.phone?.formatted,
              fax: form.data?.org?.fax?.formatted,
              orgTypeCd: getDiOrgTypes(form),
            },
          };
        } else if (summary?.recordCategoryType === "di" && summary.requestType === "update") {
          const request = submission as IDIUpdateRequest;
          const data = request.data;
          hints = {
            di: {
              merge: data.appForm?.merge,
              isMerge: data.appForm.isMerge,
            },
            org: {
              phone: data?.org?.phone?.formatted,
              fax: data?.org?.fax?.formatted,
            },
          };
        }

        /**
         * After Code Control submits a first draft of the data to XPM
         * we don't have to map the application form data to the operation form data
         * as we already have a working copy saved from the draft.  In this case, retrieve
         * the draft data from s3 and set to the working form data.
         */
        const requestFile = summary?.operationSubmission;
        if (requestFile) {
          request = (await getFile(requestFile)) as IOpsRequest<IDIRecord>;
        } else {
          if (summary.recordCategoryType === "di" && summary.requestType === "create") {
            const form = submission as IDIRequest;
            submitted = {
              ...submission.data.org,
              ...(await mapDiCreate(form)),
            };
          } else if (summary.recordCategoryType === "di" && summary.requestType === "update") {
            const request = submission as IDIUpdateRequest; //request is submission request from submitter
            const data = request.data;
            if (!data) throw new Error("Missing submission data, record is malformed.");
            const mapped = await mapDiUpdate(data); // mapped is user submitted data that is mapped to the operations form
            let diRecord: IDIRecord = await findDi(summary.orgId); //operation form data from DB for org,di
            console.log(mergeOrg(diRecord.org || {}, mapped.org || {}, "normal"));
            console.log(mergeDi(diRecord.di || {}, mapped.di || {}, "normal"));
            let mergedDi = mergeDi(diRecord.di || {}, mapped.di || {}, "normal");
            //if merge is true, check isMerge and make sure all indicators are false
            let programIndicators = data.appForm?.isMerge ? { apEligibilityInd: false, satEligibilityInd: false } : {};
            submitted = {
              org: mergeOrg(diRecord.org || {}, mapped.org || {}, "normal"),
              di: {
                ...mergedDi,
                diMergedCode: data.appForm?.merge?.diCode,
                ...programIndicators,
              },
            };
            original = diRecord;
          }
          request = {
            submitted: submitted,
            request: submitted,
            original, //original will get set as part of uppdate request logic
          } as IOpsRequest<IDIRecord>;
        }
        preProcessData(request); //to convert certain fields to initcaps from all caps before UI display
        setData({ request: request, hints: hints });
      } catch (error: any) {
        setError(error?.message || error);
      }

      setLoading(false);
    })();
  }, [summary]);

  return { request: data.request, hints: data.hints, isLoading, error };
};

interface IOpsFormProps {
  summary: ISummary;
  onComplete: (summary: ISummary) => void;
  onClose: () => void;
}

const AILoader: FC<IOpsFormProps> = ({ summary, onComplete, onClose }) => {
  const action = summary.requestType;
  const status = summary.processStatus;

  let stage: "prepare" | "execute" | "completed" = status === "completed" ? "completed" : status === "opsApproved" ? "execute" : "prepare";

  // Can we use the status to determine if this needs to re-read the data from the submission and overwrite
  // the active record.  Do we need to clear the last `operation` filePath when sending back to the user/operations
  const { request, hints, isLoading, error: fileError } = useAI(summary);
  if (isLoading) {
    return <Loader />;
  } else if (fileError) {
    return <ErrorNotice errorMessage={fileError} />;
  } else {
    return (
      <AiForm
        onComplete={() => {
          onComplete(summary);
        }}
        onClose={() => {
          onClose();
        }}
        summary={summary}
        action={action === "create" ? "create" : "update"}
        stage={stage}
        notes={summary.adminNotes || []}
        hints={hints}
        initial={request}
      />
    );
  }
};

const DILoader: FC<IOpsFormProps> = ({ summary, onComplete, onClose }) => {
  const action = summary.requestType;
  const status = summary.processStatus;

  let stage: "prepare" | "execute" | "completed" = status === "completed" ? "completed" : status === "opsApproved" ? "execute" : "prepare";

  // Can we use the status to determine if this needs to re-read the data from the submission and overwrite
  // the active record.  Do we need to clear the last `operation` filePath when sending back to the user/operations
  const { request, hints, isLoading, error: fileError } = useDI(summary);
  if (isLoading) {
    return <Loader />;
  } else if (fileError) {
    return <ErrorNotice errorMessage={fileError} />;
  } else {
    return (
      <DiForm
        onComplete={() => {
          onComplete(summary);
        }}
        onClose={() => {
          onClose();
        }}
        summary={summary}
        action={action === "create" ? "create" : "update"}
        stage={stage}
        notes={summary.adminNotes || []}
        hints={hints}
        initial={request}
      />
    );
  }
};

/**
 * This form is responsible for retrieving the files from the summary
 * and mapping the data to a standard field set for each of the possible
 * subforms.
 */
export const OpsForm: FC<IOpsFormProps> = ({ summary, onComplete, onClose }) => {
  const type = summary.recordCategoryType || "ai";
  if (type === "ai") {
    return (
      <AILoader
        onComplete={() => {
          onComplete(summary);
        }}
        onClose={() => {
          onClose();
        }}
        summary={summary}
      />
    );
  } else if (type === "di") {
    return (
      <DILoader
        onComplete={() => {
          onComplete(summary);
        }}
        onClose={() => {
          onClose();
        }}
        summary={summary}
      />
    );
  } else {
    return <ErrorNotice errorMessage={"Must be  of type - either AI or DI"} />;
  }
};
